import React from "react"
import PropTypes from "prop-types"
import { useNavigate } from "react-router-dom"
import { gql, useQuery } from "@apollo/client"

// Components
import CircularProgress from "@mui/material/CircularProgress"
import Button from "Components/UiKit/Button"

// Queries
import { GET_REFERENTIALS_UX } from "Pages/Referentials/graphql/queries/getUx"

// Helpers
import { format } from "date-fns"

// Styles
import {
  FieldLabel,
  FieldValue,
  EmptyState,
  Container,
  FieldRow,
  Card,
} from "Components/chartsLibrary/LastSampleWidget/index.style"

const parseSelect = raw => {
  if (!raw) return {}
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return raw
}

const GET_SAMPLES = gql`
  query queryReferentialSamples(
    $idReferential: String!
    $pageSize: Int!
    $page: Int!
    $filter: [FilterInput!]
    $sort: [SortInput!]
    $at: DateTime
  ) {
    queryReferentialSamples(
      idReferential: $idReferential
      pageSize: $pageSize
      page: $page
      filter: $filter
      sort: $sort
      at: $at
    ) {
      items {
        idSample
        reference
        select
        kpis
      }
    }
  }
`

const LastSampleWidget = ({
  lastSampleReferentialId = "",
  lastSampleProperties,
  primaryActionColor,
  languageId,
  configMode,
}) => {
  const { data, loading } = useQuery(GET_SAMPLES, {
    variables: {
      idReferential: lastSampleReferentialId,
      pageSize: 1,
      page: 1,
      sort: [{ colId: "__id", order: "desc" }],
    },
    skip: !lastSampleReferentialId,
    fetchPolicy: "cache-and-network",
  })
  const { data: uxData } = useQuery(GET_REFERENTIALS_UX, {
    variables: { referentialId: lastSampleReferentialId },
    skip: !lastSampleReferentialId,
  })

  const jsonForm = uxData?.referential?.ux?.createForm?.jsonForm
  const properties = jsonForm?.properties || {}

  const navigate = useNavigate()

  if (!lastSampleReferentialId) {
    return (
      <Container>
        <EmptyState>Sélectionner un référentiel</EmptyState>
      </Container>
    )
  }

  if (loading) {
    return (
      <Container>
        <CircularProgress size={24} style={{ color: primaryActionColor || "#1976d2" }} />
      </Container>
    )
  }

  const sample = data?.queryReferentialSamples?.items?.[0]

  if (!sample) {
    return (
      <Container>
        <EmptyState>Aucun sample</EmptyState>
      </Container>
    )
  }

  const handleViewSample = () => {
    navigate(`/synthesis?idReferential=${lastSampleReferentialId}&idSample=${sample.idSample}`)
  }

  const hasProperties = Array.isArray(lastSampleProperties) && lastSampleProperties.length > 0
  const selectData = parseSelect(sample.select)?.[0]

  return (
    <Container>
      <Card>
        {hasProperties ? (
          lastSampleProperties.map(key => {
            const isDate = properties?.[key]?.format_date

            return (
              <FieldRow key={key}>
                <FieldLabel>{properties?.[key]?.title || key}</FieldLabel>
                <FieldValue>
                  {isDate
                    ? selectData?.[key]
                      ? format(new Date(selectData?.[key]), properties?.[key]?.format_date)
                      : "N/A"
                    : String(selectData?.[key] ?? "N/A")}
                </FieldValue>
              </FieldRow>
            )
          })
        ) : (
          <>
            <FieldRow>
              <FieldLabel>Référence</FieldLabel>
              <FieldValue>{sample.reference}</FieldValue>
            </FieldRow>
            <FieldRow>
              <FieldLabel>ID Sample</FieldLabel>
              <FieldValue>{sample.idSample}</FieldValue>
            </FieldRow>
          </>
        )}
        <div
          style={{ width: "100%", display: "flex", justifyContent: "center", marginTop: "10px" }}
        >
          <Button onClick={handleViewSample} height="30px" type="primary">
            Voir la synthèse
          </Button>
        </div>
      </Card>
    </Container>
  )
}

LastSampleWidget.propTypes = {
  lastSampleReferentialId: PropTypes.string,
  lastSampleProperties: PropTypes.arrayOf(PropTypes.string),
  primaryActionColor: PropTypes.string,
  languageId: PropTypes.number,
  configMode: PropTypes.bool,
}

LastSampleWidget.defaultProps = {
  lastSampleReferentialId: "",
  lastSampleProperties: [],
  primaryActionColor: "",
  languageId: 1,
  configMode: false,
}

export default LastSampleWidget
